<template>
    <div class="dark:text-white">
        <button @click="$router.back()" class="mb-4 text-sm text-indigo-500 hover:underline">
            ← Voltar
        </button>

        <ticto-loading v-if="loading" />

        <div v-else-if="request" class="grid grid-cols-1 gap-6 lg:grid-cols-3">
            <div class="space-y-6 lg:col-span-2">
                <div class="rounded border bg-white p-6 dark:border-gray-700 dark:bg-gray-900">
                    <h2 class="mb-4 text-lg font-bold">Solicitação #{{ request.id }}</h2>

                    <div class="grid grid-cols-2 gap-4 text-sm">
                        <div>
                            <p class="text-xs text-gray-500">Status</p>
                            <span :class="statusBadgeClass(request.status)" class="rounded px-2 py-1 text-xs font-medium">
                                {{ statusLabel(request.status) }}
                            </span>
                        </div>
                        <div>
                            <p class="text-xs text-gray-500">Tipo</p>
                            <span v-if="request.needs_support" class="rounded bg-orange-100 px-2 py-1 text-xs text-orange-700">Suporte</span>
                            <span v-else class="rounded bg-blue-100 px-2 py-1 text-xs text-blue-700">Self-service</span>
                        </div>
                        <div class="col-span-2">
                            <p class="text-xs text-gray-500">E-mail atual</p>
                            <p class="font-mono">{{ request.source_email }}</p>
                        </div>
                        <div class="col-span-2">
                            <p class="text-xs text-gray-500">Novo e-mail</p>
                            <p class="font-mono">{{ request.target_email }}</p>
                        </div>
                        <div>
                            <p class="text-xs text-gray-500">Solicitado por</p>
                            <p>{{ request.requested_by?.name ?? "—" }} (ID {{ request.requested_by_user_id }})</p>
                        </div>
                        <div>
                            <p class="text-xs text-gray-500">Data da solicitação</p>
                            <p>{{ request.created_at }}</p>
                        </div>
                        <div v-if="request.approved_by_user_id">
                            <p class="text-xs text-gray-500">Atendido por</p>
                            <p>{{ request.approved_by?.name ?? "—" }}</p>
                        </div>
                        <div v-if="request.processed_at">
                            <p class="text-xs text-gray-500">Processado em</p>
                            <p>{{ request.processed_at }}</p>
                        </div>
                        <div v-if="request.rejection_reason" class="col-span-2">
                            <p class="text-xs text-gray-500">Motivo da rejeição</p>
                            <p class="rounded bg-red-50 p-2 text-red-700">{{ request.rejection_reason }}</p>
                        </div>
                    </div>

                    <div v-if="request.status === 'pending_support'" class="mt-6 flex gap-2 border-t pt-4 dark:border-gray-700">
                        <button
                            @click="approve"
                            :disabled="acting"
                            class="rounded bg-green-600 px-4 py-2 text-white hover:bg-green-700 disabled:opacity-50"
                        >
                            Aprovar e processar
                        </button>
                        <button
                            @click="openRejectModal"
                            :disabled="acting"
                            class="rounded bg-red-600 px-4 py-2 text-white hover:bg-red-700 disabled:opacity-50"
                        >
                            Rejeitar
                        </button>
                    </div>
                </div>

                <div class="rounded border bg-white p-6 dark:border-gray-700 dark:bg-gray-900">
                    <h3 class="mb-3 text-base font-bold">Acessos do Mozart envolvidos</h3>

                    <div v-if="request.accesses_preview?.length" class="mb-3 rounded bg-blue-50 p-3 text-xs text-blue-800 dark:bg-blue-900/30 dark:text-blue-200">
                        <p v-if="request.target_user">
                            Os acessos abaixo serão <strong>migrados</strong> para o usuário existente
                            <strong>{{ request.target_user.email }}</strong> (User #{{ request.target_user.id }})
                            ao aprovar a solicitação.
                        </p>
                        <p v-else>
                            O e-mail destino <strong>{{ request.target_email }}</strong> ainda não existe no Mozart.
                            Ao aprovar, o e-mail do usuário atual será simplesmente atualizado e os acessos permanecerão com ele.
                        </p>
                    </div>

                    <div v-else-if="request.access_migrations?.length" class="mb-3 rounded bg-green-50 p-3 text-xs text-green-800 dark:bg-green-900/30 dark:text-green-200">
                        <p v-if="request.access_migrations.some((m) => m.action === 'migrate')">
                            Os acessos abaixo foram <strong>migrados</strong> para o usuário existente
                            <strong>{{ request.target_email }}</strong> no Mozart.
                        </p>
                        <p v-else>
                            O e-mail do usuário foi atualizado para <strong>{{ request.target_email }}</strong> no Mozart
                            e os acessos abaixo permaneceram com ele.
                        </p>
                    </div>

                    <p v-if="!request.accesses_preview?.length && !request.access_migrations?.length" class="text-sm text-gray-500">
                        {{ request.status === "processed" ? "Nenhum acesso registrado nesta solicitação." : "Nenhum acesso vinculado." }}
                    </p>
                    <table v-else class="min-w-full text-sm">
                        <thead>
                            <tr class="border-b text-xs uppercase text-gray-500 dark:border-gray-700">
                                <th class="py-2 text-left">Access ID</th>
                                <th class="py-2 text-left">Portal</th>
                                <th class="py-2 text-left">Turma</th>
                                <th class="py-2 text-left">De</th>
                                <th class="py-2 text-left">Para</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="m in request.access_migrations" :key="'m-' + m.id" class="border-b dark:border-gray-700">
                                <td class="py-2">#{{ m.access_id }}</td>
                                <td class="py-2">{{ m.portal_name ?? "Portal #" + m.portal_id }}</td>
                                <td class="py-2">{{ m.classroom_name ?? "-" }}</td>
                                <td class="py-2">{{ request.source_email }}</td>
                                <td class="py-2 text-green-600">{{ request.target_email }}</td>
                            </tr>
                            <tr v-for="a in request.accesses_preview" :key="'a-' + a.access_id" class="border-b dark:border-gray-700">
                                <td class="py-2">#{{ a.access_id }}</td>
                                <td class="py-2">{{ a.portal_name }}</td>
                                <td class="py-2">{{ a.classroom_name ?? "—" }}</td>
                                <td class="py-2">{{ a.current_user_email }}</td>
                                <td class="py-2 text-green-600">{{ a.target_user_email }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <div>
                <div class="rounded border bg-white p-6 dark:border-gray-700 dark:bg-gray-900">
                    <h3 class="mb-3 text-base font-bold">Histórico</h3>
                    <ul class="space-y-3 text-sm">
                        <li v-for="log in request.logs" :key="log.id" class="border-l-2 border-indigo-300 pl-3">
                            <p class="font-medium">{{ actionLabel(log.action) }}</p>
                            <p class="text-xs text-gray-500">
                                {{ log.created_at }}
                                <span v-if="log.user">— {{ log.user.name }}</span>
                            </p>
                            <p v-if="log.notes" class="text-xs italic text-gray-600">{{ log.notes }}</p>
                        </li>
                    </ul>
                </div>
            </div>
        </div>

        <modal :open="rejectModalOpen" size="md" @close="rejectModalOpen = false">
            <h3 class="mb-4 text-lg font-bold">Rejeitar solicitação</h3>
            <p class="mb-2 text-sm">Informe o motivo da rejeição:</p>
            <textarea
                v-model="rejectReason"
                rows="4"
                class="w-full rounded border px-3 py-2 dark:bg-gray-800"
                placeholder="Motivo..."
            ></textarea>
            <div class="mt-4 flex justify-end gap-2">
                <button @click="rejectModalOpen = false" class="rounded border px-4 py-2">Cancelar</button>
                <button
                    @click="reject"
                    :disabled="acting || rejectReason.length < 5"
                    class="rounded bg-red-600 px-4 py-2 text-white disabled:opacity-50"
                >
                    Confirmar rejeição
                </button>
            </div>
        </modal>
    </div>
</template>

<script>
import { useToast } from "vue-toastification";
import Modal from "../../components/layouts/Modal.vue";
import TictoLoading from "../../components/layouts/TictoLoading.vue";

const STATUS_LABELS = {
    pending_user_confirmation: "Aguardando cliente",
    pending_support: "Aguardando atendimento",
    confirmed: "Confirmada",
    processed: "Processada",
    rejected: "Rejeitada",
    expired: "Expirada",
};

const STATUS_CLASSES = {
    pending_user_confirmation: "bg-yellow-100 text-yellow-700",
    pending_support: "bg-orange-100 text-orange-700",
    confirmed: "bg-blue-100 text-blue-700",
    processed: "bg-green-100 text-green-700",
    rejected: "bg-red-100 text-red-700",
    expired: "bg-gray-100 text-gray-700",
};

const ACTION_LABELS = {
    created: "Solicitação criada",
    email_sent: "E-mail de confirmação enviado",
    link_clicked: "Cliente clicou no link",
    confirmed: "Cliente confirmou",
    rejected: "Rejeitada",
    approved: "Aprovada pelo atendimento",
    processed: "Processada (efetivada)",
    expired: "Expirada",
};

export default {
    components: { Modal, TictoLoading },
    setup() {
        return { toast: useToast() };
    },
    data() {
        return {
            loading: true,
            acting: false,
            request: null,
            rejectModalOpen: false,
            rejectReason: "",
        };
    },
    mounted() {
        this.fetch();
    },
    methods: {
        fetch() {
            this.loading = true;
            axios
                .get(`/api/producer-email-change-requests/${this.$route.params.request_id}`)
                .then(({ data }) => {
                    this.request = data;
                })
                .catch(() => {
                    this.toast.error("Erro ao carregar solicitação");
                    this.$router.push({ name: "producer-email-change-requests" });
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        approve() {
            if (!confirm("Confirma a aprovação? A troca será efetivada nos sistemas.")) return;
            this.acting = true;
            axios
                .post(`/api/producer-email-change-requests/${this.request.id}/approve`)
                .then(() => {
                    this.toast.success("Solicitação aprovada e processada!");
                    this.fetch();
                })
                .catch((e) => this.toast.error(e.response?.data?.message ?? "Erro ao aprovar"))
                .finally(() => (this.acting = false));
        },
        openRejectModal() {
            this.rejectReason = "";
            this.rejectModalOpen = true;
        },
        reject() {
            this.acting = true;
            axios
                .post(`/api/producer-email-change-requests/${this.request.id}/reject`, { reason: this.rejectReason })
                .then(() => {
                    this.toast.success("Solicitação rejeitada");
                    this.rejectModalOpen = false;
                    this.fetch();
                })
                .catch((e) => this.toast.error(e.response?.data?.message ?? "Erro ao rejeitar"))
                .finally(() => (this.acting = false));
        },
        statusLabel(s) {
            return STATUS_LABELS[s] ?? s;
        },
        statusBadgeClass(s) {
            return STATUS_CLASSES[s] ?? "bg-gray-100 text-gray-700";
        },
        actionLabel(a) {
            return ACTION_LABELS[a] ?? a;
        },
    },
};
</script>
